/**
 * Main Layout Component
 * Provides the main application layout with navigation and content area
 */

import React, { ReactNode, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Breadcrumb } from '@/components/Navigation/Breadcrumb';
import { GlobalSearch } from '@/components/Navigation/GlobalSearch';
import {
  HomeIcon,
  BuildingOfficeIcon,
  CalculatorIcon,
  Cog6ToothIcon,
  UsersIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const navigation: NavigationItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
    description: 'Panel principal',
  },
  {
    name: 'Organizaciones',
    href: '/organizations',
    icon: BuildingOfficeIcon,
    description: 'Gestión de hogar de adulto mayor',
  },
  {
    name: 'Cálculos',
    href: '/calculations',
    icon: CalculatorIcon,
    description: 'Cálculos de personal y costos',
  },
  {
    name: 'Reportes',
    href: '/reports',
    icon: ChartBarIcon,
    description: 'Reportes y análisis',
  },
  {
    name: 'Análisis Histórico',
    href: '/analysis',
    icon: ChartBarIcon,
    description: 'Comparaciones y tendencias',
  },
  {
    name: 'Gestión de Datos',
    href: '/data-management',
    icon: CircleStackIcon,
    description: 'Persistencia, respaldo e integridad',
  },
  {
    name: 'Configuración',
    href: '/configuration',
    icon: Cog6ToothIcon,
    description: 'Configuración del sistema',
  },
  {
    name: 'Usuarios',
    href: '/users',
    icon: UsersIcon,
    description: 'Gestión de usuarios',
  },
];

interface MainLayoutProps {
  children?: ReactNode;
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const isCurrentPath = (href: string) => {
    return (
      location.pathname === href || location.pathname.startsWith(href + '/')
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Navegación principal"
      >
        <div className="flex h-full flex-col overflow-hidden">
          {/* Logo and title */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <CalculatorIcon
                  className="w-5 h-5 text-white"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900">
                  Sistema de Costeo
                </h1>
                <p className="text-xs text-gray-500">Hogar de Adulto Mayor</p>
              </div>
            </div>
            <button
              className="lg:hidden p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={() => setSidebarOpen(false)}
              aria-label="Cerrar menú de navegación"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Search */}
          <div className="md:hidden px-4 py-3 border-b border-gray-200">
            <GlobalSearch />
          </div>

          {/* Navigation */}
          <nav
            className="flex-1 px-4 py-6 space-y-2 overflow-y-auto"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db transparent',
            }}
            aria-label="Navegación principal"
          >
            {navigation.map(item => {
              const isActive = isCurrentPath(item.href);
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={`${item.name} - ${item.description}`}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-primary-700' : 'text-gray-400'
                    }`}
                    aria-hidden="true"
                  />
                  <div className="text-left flex-1 min-w-0">
                    <div className="truncate">{item.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {item.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>

          {/* User menu */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0">
            <div className="flex items-center mb-3">
              <div
                className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center"
                role="img"
                aria-label="Avatar del usuario"
              >
                <span className="text-sm font-medium text-gray-700">
                  {user?.displayName?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.displayName || 'Usuario'}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>

            <nav className="space-y-1" aria-label="Menú de usuario">
              <button
                onClick={() => {
                  navigate('/profile');
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  isCurrentPath('/profile')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
                aria-current={isCurrentPath('/profile') ? 'page' : undefined}
              >
                <UserIcon
                  className="mr-3 h-5 w-5 text-gray-400 flex-shrink-0"
                  aria-hidden="true"
                />
                Mi Perfil
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Cerrar sesión y salir de la aplicación"
              >
                <ArrowRightOnRectangleIcon
                  className="mr-3 h-5 w-5 text-gray-400 flex-shrink-0"
                  aria-hidden="true"
                />
                Cerrar Sesión
              </button>
            </nav>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4">
              <button
                className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={() => setSidebarOpen(true)}
                aria-label="Abrir menú de navegación"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>

              {/* Search - Hidden on mobile, shown on larger screens */}
              <div className="hidden md:block flex-1 max-w-lg">
                <GlobalSearch />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <span className="hidden sm:block text-sm text-gray-500">
                Bienvenido, {user?.displayName}
              </span>
            </div>
          </div>

          {/* Breadcrumb */}
          <div className="px-4 pb-3">
            <Breadcrumb />
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">{children || <Outlet />}</div>
        </main>
      </div>
    </div>
  );
};
